/**
 * Menu Vues sauvegardées (Fév 2026).
 * Permet de sauvegarder/recharger/supprimer les vues (filtres actifs) côté backend.
 *
 * Props :
 *   - scope: "bank" | "wave" | "unified" | "treasury"
 *   - currentFilters: objet représentant les filtres actifs (period, account_id, kpiFilter, etc.)
 *   - onLoad(filters): callback appelé quand une vue est chargée
 *
 * Endpoints : /api/saved-views
 */
import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { Bookmark, Save, Trash2, Loader2, ChevronDown, Share2 } from 'lucide-react';
import { toast } from '../../hooks/use-toast';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function SavedViewsMenu({ scope, currentFilters, onLoad }) {
  const [open, setOpen] = useState(false);
  const [views, setViews] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [newName, setNewName] = useState('');
  const [isShared, setIsShared] = useState(false);
  const [showForm, setShowForm] = useState(false);

  const fetchViews = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/saved-views`, { params: { scope } });
      setViews(data?.items || []);
    } catch (e) {
      // silent
    } finally {
      setLoading(false);
    }
  }, [scope]);

  useEffect(() => { if (open) fetchViews(); }, [open, fetchViews]);

  const saveNew = async () => {
    if (!newName.trim()) {
      toast({ title: 'Nom requis', variant: 'destructive' });
      return;
    }
    setSaving(true);
    try {
      await axios.post(`${API}/saved-views`, {
        scope, name: newName.trim(), filters: currentFilters || {}, is_shared: isShared,
      });
      toast({ title: 'Vue sauvegardée', description: `"${newName.trim()}" enregistrée.` });
      setNewName(''); setIsShared(false); setShowForm(false);
      await fetchViews();
    } catch (e) {
      toast({ title: 'Erreur sauvegarde', description: e.response?.data?.detail || e.message, variant: 'destructive' });
    } finally { setSaving(false); }
  };

  const deleteView = async (id) => {
    if (!window.confirm('Supprimer cette vue sauvegardée ?')) return;
    try {
      await axios.delete(`${API}/saved-views/${id}`);
      await fetchViews();
      toast({ title: 'Vue supprimée' });
    } catch (e) {
      toast({ title: 'Erreur suppression', description: e.response?.data?.detail || e.message, variant: 'destructive' });
    }
  };

  const applyView = (v) => {
    onLoad?.(v.filters || {});
    toast({ title: 'Vue appliquée', description: v.name });
    setOpen(false);
  };

  return (
    <div className="relative" data-testid="saved-views-menu">
      <button
        onClick={() => setOpen(v => !v)}
        className="inline-flex items-center gap-1.5 bg-white border border-slate-200 rounded-lg px-3 py-1.5 text-sm text-slate-700 hover:bg-slate-50"
        data-testid="saved-views-toggle"
      >
        <Bookmark className="h-4 w-4 text-blue-600" />
        <span className="font-medium">Vues</span>
        {views.length > 0 && <span className="text-[10px] bg-blue-100 text-blue-700 px-1.5 py-0.5 rounded-full">{views.length}</span>}
        <ChevronDown className="h-3.5 w-3.5 text-slate-400" />
      </button>

      {open && (
        <div className="absolute right-0 mt-1 w-80 bg-white border border-slate-200 rounded-lg shadow-xl z-40 max-h-[500px] overflow-hidden"
             data-testid="saved-views-dropdown">
          <div className="p-3 border-b border-slate-100 flex items-center justify-between">
            <span className="text-sm font-semibold text-slate-800">Vues sauvegardées</span>
            <button
              onClick={() => setShowForm(v => !v)}
              className="text-xs text-blue-600 hover:text-blue-800 inline-flex items-center gap-1"
              data-testid="saved-views-new-btn"
            >
              <Save className="h-3.5 w-3.5" /> Enregistrer
            </button>
          </div>

          {showForm && (
            <div className="p-3 border-b border-slate-100 bg-slate-50/50 space-y-2" data-testid="saved-views-form">
              <input
                type="text"
                value={newName}
                onChange={(e) => setNewName(e.target.value)}
                placeholder="Nom de la vue (ex: Q1 2026 non rapprochées)"
                className="w-full text-sm border border-slate-200 rounded px-2 py-1.5"
                maxLength={80}
                data-testid="saved-views-name-input"
              />
              <label className="flex items-center gap-2 text-xs text-slate-600 cursor-pointer">
                <input type="checkbox" checked={isShared} onChange={(e) => setIsShared(e.target.checked)}
                       data-testid="saved-views-shared-checkbox" />
                <Share2 className="h-3 w-3" /> Partager avec l'équipe
              </label>
              <div className="flex gap-2">
                <button
                  onClick={saveNew}
                  disabled={saving || !newName.trim()}
                  className="flex-1 bg-blue-600 hover:bg-blue-700 text-white text-xs px-3 py-1.5 rounded disabled:opacity-50 inline-flex items-center justify-center gap-1"
                  data-testid="saved-views-save-btn"
                >
                  {saving && <Loader2 className="h-3 w-3 animate-spin" />} Sauvegarder
                </button>
                <button
                  onClick={() => { setShowForm(false); setNewName(''); setIsShared(false); }}
                  className="text-xs px-3 py-1.5 border border-slate-200 rounded text-slate-600 hover:bg-slate-100"
                >
                  Annuler
                </button>
              </div>
            </div>
          )}

          <div className="max-h-80 overflow-y-auto divide-y divide-slate-50">
            {loading ? (
              <div className="p-4 text-center text-xs text-slate-400">
                <Loader2 className="h-4 w-4 animate-spin inline mr-1" /> Chargement…
              </div>
            ) : views.length === 0 ? (
              <div className="p-4 text-center text-xs text-slate-400">
                Aucune vue sauvegardée pour ce module.
              </div>
            ) : views.map(v => (
              <div key={v.id} className="flex items-center gap-2 p-2 hover:bg-slate-50" data-testid={`saved-view-item-${v.id}`}>
                <button
                  onClick={() => applyView(v)}
                  className="flex-1 min-w-0 text-left"
                  data-testid={`saved-view-apply-${v.id}`}
                >
                  <div className="text-sm text-slate-800 truncate flex items-center gap-1.5">
                    {v.name}
                    {v.is_shared && <Share2 className="h-3 w-3 text-blue-500 flex-shrink-0" title="Partagée" />}
                  </div>
                  <div className="text-[10px] text-slate-400 truncate">
                    {Object.keys(v.filters || {}).length} filtre(s) · {v.user_email}
                  </div>
                </button>
                <button
                  onClick={() => deleteView(v.id)}
                  className="text-slate-400 hover:text-rose-600 flex-shrink-0 p-1"
                  data-testid={`saved-view-delete-${v.id}`}
                  title="Supprimer"
                >
                  <Trash2 className="h-3.5 w-3.5" />
                </button>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
